import { useState, useEffect, useCallback, useMemo, type RefObject } from 'react'
import { MaterialIcon } from '@/ui/material-icon'
import { Button } from '@/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select'
import { Input } from '@/ui/input'
import { Label } from '@/ui/label'
import {
  useIntegrations,
  buildInitialFormState,
} from '@/features/adp/hooks/useIntegrations'
import type { DynamicField } from '@/features/adp/api/integrations.api'
import {
  applySessionOptionsToFields,
  computeIntegrationChainOutputs,
  getColumnOptionsForIntegrationField,
  getSessionColumnOptions,
  getSessionTableOptions,
  getTableOptionsForIntegrationRow,
  getTableOptionsForLinkedSourceRow,
  integrationFieldLooksLikePrimaryTable,
  integrationFieldLooksLikeSecondaryTable,
  integrationFieldLooksLikeTable,
  mergeSavedForm,
  persistIntegrationChainOutputs,
  readAdpPipelineSession,
  upsertIntegrationOutputAssetSession,
  upsertIntegrationSession,
  useAdpPipelineSessionSnapshot,
} from '@/features/adp/utils/adpPipelineSessionStorage'
import type { PipelineGraphBridge } from '@/features/adp/components/flow/pipelineGraphBridge.types'
import styles from './JoinDetailsSheet.module.scss'

export type JoinDetailsSheetProps = {
  onClose: () => void
  configuringNodeId?: string | null
  /** Palette integration id: `join` shows join form; `union` shows union form */
  connectorId?: string
  /** Source asset nodes connected into this join/union (ordered left → right). */
  upstreamSourceNodeIds?: string[]
  /** Adds a “Joined” / “Union” result node on the canvas after save. */
  graphBridgeRef?: RefObject<PipelineGraphBridge | null>
}

type FormState = Record<string, any>

type JoinOperationRow = {
  TransformationNo: string
  JoinType: string
  SourceTable: string
  SourceTableKey: string
  SourceTableGetColumns: string
  JoinTableName: string
  JoinTable_DataSource: string
  JoinTable_isMaster: string
  JoinTableKey: string
  JoinTableGetColumns: string
}

type UnionOperationRow = {
  TransformationNo: 'UN01'
  SourceTable: string
  WithTable: string
  WithTable_DataSource: string
  WithTable_isMaster: string
  WithDuplicates: string
  WithoutDuplicates: string
  SortBy: null
}

const FALLBACK_SCHEMA_OPTIONS = [
  'id',
  'employee_id',
  'dept_id',
  'name',
  'email',
  'department',
] as const

function pickStringByPattern(values: Record<string, unknown>, pattern: RegExp): string {
  const v = Object.entries(values).find(([key, value]) => {
    if (!pattern.test(key)) return false
    return typeof value === 'string' && value.trim().length > 0
  })?.[1]
  return typeof v === 'string' ? v.trim() : ''
}

function normalizeYesNo(value: string, fallback: 'Yes' | 'No'): string {
  const n = value.trim().toLowerCase()
  if (!n) return fallback
  if (['yes', 'y', 'true', '1'].includes(n)) return 'Yes'
  if (['no', 'n', 'false', '0'].includes(n)) return 'No'
  return value
}

function toCsv(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map(v => String(v ?? '').trim()).filter(Boolean).join(',')
  }
  if (typeof value === 'string') return value.trim()
  return ''
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function getJoinConfigRow(form: Record<string, unknown>): Record<string, unknown> | null {
  const joinConfig = form.joinConfig
  if (Array.isArray(joinConfig) && joinConfig.length > 0) {
    const first = asRecord(joinConfig[0])
    if (first) return first
  }
  return asRecord(joinConfig)
}

function getJoinConfigRows(form: Record<string, unknown>): Record<string, unknown>[] {
  const joinConfig = form.joinConfig
  if (Array.isArray(joinConfig)) {
    return joinConfig.map(asRecord).filter((row): row is Record<string, unknown> => !!row)
  }
  const singleRow = asRecord(joinConfig)
  if (singleRow) return [singleRow]
  return []
}

function getUnionConfigRow(form: Record<string, unknown>): Record<string, unknown> | null {
  const unionConfig = form.unionConfig
  if (Array.isArray(unionConfig) && unionConfig.length > 0) {
    const first = asRecord(unionConfig[0])
    if (first) return first
  }
  return asRecord(unionConfig)
}

function resolveSessionAssetByTableValue(
  tableValue: string,
  assets: ReturnType<typeof readAdpPipelineSession>['assets']
) {
  const trimmed = tableValue.trim()
  if (!trimmed) return null

  if (trimmed.includes('::')) {
    const [nodeId, tableName] = trimmed.split('::', 2)
    const fromNode = assets[nodeId]
    if (fromNode && fromNode.tableName === tableName) return fromNode
  }

  const exactTableMatch = Object.values(assets).find(asset => asset.tableName === trimmed)
  if (exactTableMatch) return exactTableMatch

  return null
}

function toSelectedColumnsCsv(
  asset: NonNullable<ReturnType<typeof resolveSessionAssetByTableValue>>
): string {
  const selected = asset.columns.filter(col => col.selected !== false)
  const list = (selected.length > 0 ? selected : asset.columns).map(col => col.name)
  return list.join(',')
}

function toJoinColumnsCsvWithoutKey(
  asset: NonNullable<ReturnType<typeof resolveSessionAssetByTableValue>>,
  joinKey: string
): string {
  const selected = asset.columns.filter(col => col.selected !== false)
  const list = (selected.length > 0 ? selected : asset.columns)
    .map(col => col.name)
    .filter(name => name !== joinKey)
  return list.join(',')
}

function pickFallbackJoinKeyFromAsset(
  asset: NonNullable<ReturnType<typeof resolveSessionAssetByTableValue>>
): string {
  const pk = asset.columns.find(col => col.isBusinessPrimaryKey)
  if (pk?.name) return pk.name
  const selected = asset.columns.find(col => col.selected !== false)
  if (selected?.name) return selected.name
  return asset.columns[0]?.name ?? ''
}

function buildJoinOperationJson(
  form: Record<string, unknown>,
  upstreamSourceNodeIds: string[] = [],
  chainSteps: { label: string; columns: { name: string; dataType: string }[] }[] = []
): Record<string, string> | null {
  const rows = getJoinConfigRows(form)
  const sources = rows.length > 0 ? rows : [getJoinConfigRow(form) ?? form]
  const joinOperationJson: Record<string, string> = {}
  let hasAtLeastOneJoin = false
  const session = readAdpPipelineSession()
  const assets = session.assets
  const upstreamAssets = upstreamSourceNodeIds
    .map(nodeId => assets[nodeId])
    .filter(Boolean)

  sources.forEach((source, index) => {
    const sourceTable = pickStringByPattern(
      source,
      /source.*table|left.*table|primary.*table|table1|first.*table/i
    )
    const sourceTableKeyRaw = pickStringByPattern(source, /source.*key|left.*key|primary.*key/i)
    const sourceColumnsRaw =
      Object.entries(source).find(([key]) => /source.*get.*columns|left.*columns|source.*columns/i.test(key))?.[1] ?? ''
    const sourceAsset =
      resolveSessionAssetByTableValue(sourceTable, assets) ?? upstreamAssets[0] ?? null
    const sourceTableKey = sourceTableKeyRaw || (sourceAsset ? pickFallbackJoinKeyFromAsset(sourceAsset) : '')
    const sourceTableGetColumns =
      toCsv(sourceColumnsRaw) ||
      (index > 0
        ? chainSteps[index - 1]?.columns?.map(col => col.name).join(',') || ''
        : sourceAsset
          ? toSelectedColumnsCsv(sourceAsset)
          : '')

    const joinTableName = pickStringByPattern(
      source,
      /join.*table|with.*table|right.*table|secondary.*table|table2/i
    )
    const joinTableDataSource = pickStringByPattern(
      source,
      /join.*table.*data.*source|with.*table.*data.*source|data.*source/i
    )
    const joinTableIsMaster = normalizeYesNo(
      pickStringByPattern(source, /join.*table.*is.*master|is.*master|master/i),
      'No'
    )
    const joinTableKeyRaw = pickStringByPattern(
      source,
      /join.*table.*key|right.*key|secondary.*key|target.*key/i
    )
    const joinColumnsRaw =
      Object.entries(source).find(([key]) =>
        /join.*table.*get.*columns|right.*columns|join.*columns/i.test(key)
      )?.[1] ?? ''
    const joinAsset =
      resolveSessionAssetByTableValue(joinTableName, assets) ?? upstreamAssets[1] ?? null
    const joinTableKey = joinTableKeyRaw || (joinAsset ? pickFallbackJoinKeyFromAsset(joinAsset) : '')
    const joinTableGetColumns =
      toCsv(joinColumnsRaw) ||
      (joinAsset ? toJoinColumnsCsvWithoutKey(joinAsset, joinTableKey) : '')

    const joinTypeRaw = pickStringByPattern(source, /join.*type|join.*style|join.*mode/i)
    const joinType = joinTypeRaw || 'left'

    if (!sourceTable || !joinTableName) return

    const transformationNo = `JR${String(index + 1).padStart(2, '0')}`
    const row: JoinOperationRow = {
      TransformationNo: transformationNo,
      JoinType: joinType,
      SourceTable: index > 0 ? 'output' : sourceTable,
      SourceTableKey: sourceTableKey,
      SourceTableGetColumns: sourceTableGetColumns,
      JoinTableName: joinTableName,
      JoinTable_DataSource: joinTableDataSource || joinAsset?.dataSourceName || '',
      JoinTable_isMaster: joinTableIsMaster,
      JoinTableKey: joinTableKey,
      JoinTableGetColumns: joinTableGetColumns,
    }
    joinOperationJson[transformationNo] = JSON.stringify([row])
    hasAtLeastOneJoin = true
  })

  return hasAtLeastOneJoin ? joinOperationJson : null
}

function normalizeYesNoLower(value: string, fallback: 'yes' | 'no'): string {
  const n = value.trim().toLowerCase()
  if (!n) return fallback
  if (['yes', 'y', 'true', '1'].includes(n)) return 'yes'
  if (['no', 'n', 'false', '0'].includes(n)) return 'no'
  return value
}

function buildUnionOperationJson(form: Record<string, unknown>): { UN01: string } | null {
  const unionConfigRow = getUnionConfigRow(form)
  const source = unionConfigRow ?? form

  const sourceTable = pickStringByPattern(
    source,
    /source.*table|left.*table|primary.*table|table1|first.*table/i
  )
  const withTable = pickStringByPattern(
    source,
    /with.*table|join.*table|right.*table|secondary.*table|table2/i
  )
  const withTableDataSource = pickStringByPattern(
    source,
    /with.*table.*data.*source|join.*table.*data.*source|data.*source/i
  )
  const withTableIsMaster = normalizeYesNoLower(
    pickStringByPattern(source, /with.*table.*is.*master|join.*table.*is.*master|is.*master|master/i),
    'no'
  )
  const withDuplicates = normalizeYesNoLower(
    pickStringByPattern(source, /with.*duplicates|allow.*duplicates|duplicates/i),
    'yes'
  )
  const withoutDuplicates = normalizeYesNoLower(
    pickStringByPattern(source, /without.*duplicates|remove.*duplicates/i),
    'no'
  )

  if (!sourceTable || !withTable) return null

  const row: UnionOperationRow = {
    TransformationNo: 'UN01',
    SourceTable: sourceTable,
    WithTable: withTable,
    WithTable_DataSource: withTableDataSource,
    WithTable_isMaster: withTableIsMaster,
    WithDuplicates: withDuplicates,
    WithoutDuplicates: withoutDuplicates,
    SortBy: null,
  }

  return { UN01: JSON.stringify([row]) }
}

// ─── FieldRenderer ────────────────────────────────────────────────

type JoinFieldRenderContext = {
  integrationNodeId: string
  connectorId: string
  chainSteps: { label: string; columns: { name: string; dataType: string }[] }[]
  upstreamSourceNodeIds?: string[]
  groupRow?: {
    groupKey: string
    rowIndex: number
    row: Record<string, unknown>
    subFields: DynamicField[]
  }
}

type FieldRendererProps = {
  field: DynamicField
  value: any
  error?: string
  onChange: (key: string, value: any) => void
  renderContext?: JoinFieldRenderContext
}

/** Puts `preferred` first (e.g. join_result, row-scoped columns), then `base`, deduped by value. */
function mergeSelectOptions(
  preferred: { value: string; label: string }[],
  base: { value: string; label: string }[] | undefined
): { value: string; label: string }[] {
  const seen = new Set<string>()
  const out: { value: string; label: string }[] = []
  for (const o of [...preferred, ...(base ?? [])]) {
    if (seen.has(o.value)) continue
    seen.add(o.value)
    out.push(o)
  }
  return out
}

function resolveUpstreamTableFieldOptions(
  field: DynamicField,
  subFields: DynamicField[],
  upstreamIds: string[],
  rowIndex: number,
  chainSteps: { label: string; columns: { name: string; dataType: string }[] }[]
): { value: string; label: string }[] {
  if (integrationFieldLooksLikePrimaryTable(field)) {
    return getTableOptionsForLinkedSourceRow(upstreamIds[0], rowIndex, chainSteps)
  }
  if (integrationFieldLooksLikeSecondaryTable(field)) {
    return upstreamIds[1]
      ? getTableOptionsForLinkedSourceRow(upstreamIds[1], rowIndex, chainSteps)
      : []
  }
  const tableFields = subFields.filter(f => integrationFieldLooksLikeTable(f))
  const idx = tableFields.findIndex(tf => tf.key === field.key)
  if (idx === 0 && upstreamIds[0]) {
    return getTableOptionsForLinkedSourceRow(upstreamIds[0], rowIndex, chainSteps)
  }
  if (idx === 1 && upstreamIds[1]) {
    return getTableOptionsForLinkedSourceRow(upstreamIds[1], rowIndex, chainSteps)
  }
  return getTableOptionsForIntegrationRow(rowIndex, chainSteps)
}

function resolveSelectOptions(
  field: DynamicField,
  ctx: JoinFieldRenderContext | undefined,
  fallback: { value: string; label: string }[]
): { value: string; label: string }[] {
  const base = field.options?.length ? field.options : fallback
  if (!ctx?.groupRow) return mergeSelectOptions([], base)

  const { row, rowIndex, subFields } = ctx.groupRow
  const rowTableOpts = getTableOptionsForIntegrationRow(rowIndex, ctx.chainSteps)
  const upstreamIds = ctx.upstreamSourceNodeIds

  if (integrationFieldLooksLikeTable(field)) {
    if (upstreamIds?.length) {
      return mergeSelectOptions(
        resolveUpstreamTableFieldOptions(field, subFields, upstreamIds, rowIndex, ctx.chainSteps),
        base
      )
    }
    return mergeSelectOptions(rowTableOpts, base)
  }

  const rowOpts = getColumnOptionsForIntegrationField(
    row,
    subFields,
    field,
    ctx.integrationNodeId,
    ctx.chainSteps,
    ctx.upstreamSourceNodeIds
  )
  // For join/union row-scoped column fields, keep options strictly row/session scoped.
  // Merging API/base options here can reintroduce mixed primary/secondary columns.
  return rowOpts.length ? rowOpts : mergeSelectOptions([], base)
}

function FieldRenderer({ field, value, error, onChange, renderContext }: FieldRendererProps) {
  const fieldId = `field-${field.key}`
  const isRequired = !!field.required

  const labelEl = (
    <Label className={styles.fieldLabel} htmlFor={fieldId}>
      {field.label} {isRequired && '*'}
    </Label>
  )

  const fallbackOpts = FALLBACK_SCHEMA_OPTIONS.map(o => ({ value: o, label: o }))

  // ── Multiselect — no shadcn equivalent, keep native ────────────
  if (field.type === 'multiselect') {
    const options = resolveSelectOptions(field, renderContext, fallbackOpts)

    return (
      <div className={styles.field}>
        {labelEl}
        <select
          id={fieldId}
          className={styles.nativeSelect}
          value={Array.isArray(value) ? value : []}
          multiple
          onChange={e => {
            const selected = Array.from(e.target.selectedOptions).map(o => o.value)
            onChange(field.key, selected)
          }}
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    )
  }

  // ── Single select ──────────────────────────────────────────────
  if (field.type === 'select') {
    const options = resolveSelectOptions(field, renderContext, fallbackOpts)

    return (
      <div className={styles.field}>
        {labelEl}
        <Select
          value={String(value ?? '')}
          onValueChange={val => onChange(field.key, val)}
        >
          <SelectTrigger className={styles.selectTrigger} id={fieldId}>
            <SelectValue placeholder={field.placeholder ?? 'Select...'} />
          </SelectTrigger>
          <SelectContent>
            {options.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    )
  }

  // ── Group ──────────────────────────────────────────────────────
  if (field.type === 'group') {
    const isRepeatable = !!(field.multiple || field.repeatable)
    const addButtonLabel = field.addButtonLabel || 'Add more'
    const allowRemove = field.allowRemove !== false

    if (!isRepeatable) {
      const groupObj =
        value && typeof value === 'object' && !Array.isArray(value)
          ? (value as Record<string, unknown>)
          : buildInitialFormState(field.fields || [])

      return (
        <div className={styles.field}>
          <Label className={styles.fieldLabel}>{field.label}</Label>
          <div className={styles.groupFields}>
            {field.fields?.map(subField => (
              <FieldRenderer
                key={subField.key}
                field={subField}
                value={groupObj[subField.key] ?? (subField.type === 'multiselect' ? [] : '')}
                error={error}
                onChange={(subKey, subValue) => {
                  onChange(field.key, { ...groupObj, [subKey]: subValue })
                }}
                renderContext={renderContext}
              />
            ))}
          </div>
          {error && <span className={styles.errorText}>{error}</span>}
        </div>
      )
    }

    const groupValue = Array.isArray(value) ? value : []

    return (
      <div className={styles.field}>
        <Label className={styles.fieldLabel}>{field.label}</Label>
        <div className={styles.groupContainer}>
          {groupValue.map((item: any, index: number) => {
            const row = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>
            const subFields = field.fields ?? []
            const rowCtx: JoinFieldRenderContext | undefined = renderContext
              ? {
                  ...renderContext,
                  groupRow: {
                    groupKey: field.key,
                    rowIndex: index,
                    row,
                    subFields,
                  },
                }
              : undefined

            return (
              <div key={index} className={styles.groupItem}>
                <div className={styles.groupFields}>
                  {subFields.map(subField => (
                    <FieldRenderer
                      key={subField.key}
                      field={subField}
                      value={row[subField.key] ?? (subField.type === 'multiselect' ? [] : '')}
                      error={error}
                      onChange={(subKey, subValue) => {
                        const newGroupValue = [...groupValue]
                        newGroupValue[index] = { ...newGroupValue[index], [subKey]: subValue }
                        onChange(field.key, newGroupValue)
                      }}
                      renderContext={rowCtx}
                    />
                  ))}
                </div>
                {allowRemove && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={styles.removeButton}
                    onClick={() => {
                      const newGroupValue = groupValue.filter((_, i) => i !== index)
                      onChange(field.key, newGroupValue)
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            )
          })}
          <Button
            type="button"
            variant="outline"
            className={styles.addButton}
            onClick={() => {
              const newItem = buildInitialFormState(field.fields || [])
              onChange(field.key, [...groupValue, newItem])
            }}
          >
            {addButtonLabel}
          </Button>
        </div>
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    )
  }

  // ── Text input (default) ───────────────────────────────────────
  return (
    <div className={styles.field}>
      {labelEl}
      <Input
        id={fieldId}
        type="text"
        className={styles.fieldInput}
        value={String(value ?? '')}
        placeholder={field.placeholder ?? ''}
        onChange={e => onChange(field.key, e.target.value)}
      />
      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  )
}

export function JoinDetailsSheet({
  onClose,
  configuringNodeId = null,
  connectorId = 'join',
  upstreamSourceNodeIds = [],
  graphBridgeRef,
}: JoinDetailsSheetProps) {
  const { selectedIntegrationType, fields, isLoading, error: apiError } =
    useIntegrations(connectorId)

  const sessionSnapshot = useAdpPipelineSessionSnapshot()

  const fieldsWithOptions = useMemo(() => {
    const tables = getSessionTableOptions()
    const cols = getSessionColumnOptions()
    return applySessionOptionsToFields(fields, tables, cols)
  }, [fields, sessionSnapshot])

  const [form, setForm] = useState<FormState>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const chainSteps = useMemo(
    () =>
      configuringNodeId
        ? computeIntegrationChainOutputs(configuringNodeId, connectorId, form, fields)
        : [],
    [configuringNodeId, connectorId, form, fields]
  )

  useEffect(() => {
    if (!configuringNodeId) return
    persistIntegrationChainOutputs(configuringNodeId, chainSteps)
  }, [configuringNodeId, chainSteps])

  useEffect(() => {
    const initial = buildInitialFormState(fields)
    const saved =
      configuringNodeId != null
        ? readAdpPipelineSession().integrations[configuringNodeId]
        : undefined
    if (saved?.connectorId === connectorId && saved.form && Object.keys(saved.form).length > 0) {
      setForm(mergeSavedForm(initial, saved.form) as FormState)
    } else {
      setForm(initial)
    }
    setErrors({})
  }, [fields, configuringNodeId, connectorId])

  const displayTitle = selectedIntegrationType?.name ?? connectorId ?? 'Integration'
  const subtitle = selectedIntegrationType?.description ?? 'Configure integration settings'

  const handleChange = useCallback(
    (key: string, value: any) => {
      setForm(prev => ({ ...prev, [key]: value }))
      if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }))
    },
    [errors]
  )

  const validate = useCallback((): boolean => {
    const next: Record<string, string> = {}
    fieldsWithOptions.forEach(field => {
      const value = form[field.key]
      if (!field.required) return
      if (field.type === 'multiselect') {
        if (!Array.isArray(value) || value.length === 0)
          next[field.key] = `${field.label} is required`
      } else if (field.type === 'group') {
        if (field.multiple || field.repeatable) {
          if (!Array.isArray(value) || value.length === 0)
            next[field.key] = `At least one ${field.label} is required`
        }
      } else if (!value || String(value).trim() === '') {
        next[field.key] = `${field.label} is required`
      }
    })
    setErrors(next)
    return Object.keys(next).length === 0
  }, [fieldsWithOptions, form])

  const handleSave = useCallback(async () => {
    if (!validate()) return
    setIsSubmitting(true)
    try {
      if (configuringNodeId) {
        const formToSave = { ...(form as Record<string, unknown>) }
        if (connectorId === 'join') {
          const joinOperationJson = buildJoinOperationJson(
            formToSave,
            upstreamSourceNodeIds,
            chainSteps
          )
          if (joinOperationJson) {
            formToSave.joinOperationJson = joinOperationJson
            // Temporary debug trace: inspect exact join payload sent to transformation_definition.
            console.debug('[ADP][join] joinOperationJson', joinOperationJson)
          }
        } else if (connectorId === 'union') {
          const unionOperationJson = buildUnionOperationJson(formToSave)
          if (unionOperationJson) {
            formToSave.unionOperationJson = unionOperationJson
          }
        }
        upsertIntegrationSession(configuringNodeId, connectorId, formToSave)
        const op: 'join' | 'union' = connectorId === 'union' ? 'union' : 'join'
        const outputNodeId = graphBridgeRef?.current?.addIntegrationOutputNode({
          integrationNodeId: configuringNodeId,
          connectorId: op,
        })
        const finalStep = chainSteps[chainSteps.length - 1]
        if (outputNodeId && finalStep?.columns?.length) {
          upsertIntegrationOutputAssetSession({
            outputNodeId,
            connectorId: op,
            integrationNodeId: configuringNodeId,
            step: finalStep,
          })
        }
      }
      await new Promise(resolve => setTimeout(resolve, 800))
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }, [validate, onClose, configuringNodeId, connectorId, form, graphBridgeRef, chainSteps])

  return (
    <aside
      className={styles.root}
      aria-label={displayTitle}
      data-configuring-node={configuringNodeId ?? undefined}
    >
      <div className={styles.inner}>
        <div className={styles.headerRow}>
          <button
            type="button"
            className={styles.closeChevron}
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden>&gt;&gt;</span>
          </button>
          <h2 className={styles.title}>{displayTitle}</h2>
        </div>

        <div className={styles.divider} />

        <p className={styles.subtitle}>{subtitle}</p>

        <div className={styles.heroIcon} aria-hidden>
          <MaterialIcon
            name={connectorId === 'join' ? 'join_full' : 'merge'}
            size={56}
          />
        </div>

        {isLoading ? (
          <p className={styles.loadingText}>Loading configuration...</p>
        ) : apiError ? (
          <p className={styles.errorText}>Error: {apiError}</p>
        ) : fieldsWithOptions.length === 0 ? (
          <p className={styles.noFieldsText}>No configuration fields available.</p>
        ) : (
          <div className={styles.fieldsContainer}>
            {fieldsWithOptions.map(field => (
              <FieldRenderer
                key={field.key}
                field={field}
                value={form[field.key] ?? ''}
                error={errors[field.key]}
                onChange={handleChange}
                renderContext={
                  configuringNodeId
                    ? {
                        integrationNodeId: configuringNodeId,
                        connectorId,
                        chainSteps,
                        upstreamSourceNodeIds,
                      }
                    : undefined
                }
              />
            ))}
          </div>
        )}

        <div className={styles.footer}>
          <Button
            type="button"
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </aside>
  )
}
